"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import {
  type Interval,
  type RecaptureInput,
  type RecaptureProblem,
  recaptureProblem,
  summarize,
} from "@/lib/tool-kit/ecology/mark-recapture";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const LINCOLN_PETERSEN_CALCULATOR_STRINGS = {
  markedLabel: "M — animals marked in the first sample",
  caughtLabel: "C — animals caught in the second sample",
  recapturedLabel: "R — marked animals among those C",
  levelLabel: "Confidence level",
  results: "Results",
  chapman: "Chapman estimate of N",
  petersen: "Lincoln–Petersen estimate of N",
  petersenNone: "Undefined (R = 0)",
  standardError: "Standard error of the Chapman estimate",
  seen: "Different animals actually seen (M + C − R)",
  markedShare: "Marked share of the second sample (R ÷ C)",
  populationMarked: "Share of the population that is marked (M ÷ N)",
  intervalsHeading: "{level}% confidence intervals for N",
  columnMethod: "Method",
  columnLower: "Lower",
  columnUpper: "Upper",
  methodNormal: "Normal approximation, Chapman estimate ± z × SE",
  methodExact: "Exact binomial (Clopper–Pearson on M ÷ N)",
  noUpper: "no upper limit",
  workingHeading: "The working",
  workingPetersen: "N = M × C ÷ R = {m} × {c} ÷ {r} = {result}",
  workingChapman:
    "N = (M + 1)(C + 1) ÷ (R + 1) − 1 = ({m} + 1)({c} + 1) ÷ ({r} + 1) − 1 = {result}",
  workingVariance:
    "Var = (M + 1)(C + 1)(M − R)(C − R) ÷ ((R + 1)²(R + 2)) = {variance}, so SE = {se}",
  noteZero:
    "No marked animals were recaptured, so the data say only that the population is large. Lincoln–Petersen is undefined, the Chapman figure is only a rough guide, and the interval has no upper limit. Catch more animals before trusting any estimate.",
  noteFew:
    "Only {r} marked animals were recaptured. With so few the estimate is imprecise and the normal interval can be misleading; prefer the exact interval, and aim for many more recaptures.",
  notePrecision:
    "Precision depends mostly on R: the relative standard error is about 1 ÷ √R, here about {percent}%.",
  noteUnbiased:
    "M + C is at least the estimate, so the Chapman estimator is unbiased here.",
  noteBiased:
    "M + C is below the estimate, so the Chapman estimator is not exactly unbiased. The bias is small when the expected number of recaptures, M × C ÷ N, is large.",
  assumptions:
    "Assumes a closed population (no births, deaths or movement between samples), no mark loss, equal chance of capture for every animal, and marks that do not change survival or behavior.",
  copyResult: "Copy result",
  copied: "Copied!",
  copySummary:
    "N ≈ {chapman} (Chapman; M = {m}, C = {c}, R = {r}); {level}% CI {lower}–{upper}",
  enter: "Enter M, C and R as whole numbers.",
  errMarked: "M must be a whole number of at least 1.",
  errCaught: "C must be a whole number of at least 1.",
  errRecaptured: "R must be a whole number of 0 or more.",
  errExceeds: "R cannot be more than M or more than C.",
} as const;

type Strings = typeof LINCOLN_PETERSEN_CALCULATOR_STRINGS;
type Level = "0.9" | "0.95" | "0.99";

const ID = "lp";
const FEW_RECAPTURES = 7;

function problemText(problem: RecaptureProblem, strings: Strings): string {
  const messages: Record<RecaptureProblem, string> = {
    marked: strings.errMarked,
    caught: strings.errCaught,
    recaptured: strings.errRecaptured,
    exceeds: strings.errExceeds,
  };
  return messages[problem];
}

export default function LincolnPetersenCalculatorWidget() {
  const strings = useToolUi(LINCOLN_PETERSEN_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [marked, setMarked] = useState("100");
  const [caught, setCaught] = useState("80");
  const [recaptured, setRecaptured] = useState("16");
  const [level, setLevel] = useState<Level>("0.95");

  const m = parseMeasuredNumber(marked);
  const c = parseMeasuredNumber(caught);
  const r = parseMeasuredNumber(recaptured);
  const input: RecaptureInput | null =
    m === null || c === null || r === null
      ? null
      : { marked: m, caught: c, recaptured: r };
  const problem = input ? recaptureProblem(input) : null;

  function body() {
    if (input === null) {
      return <Notice>{strings.enter}</Notice>;
    }
    if (problem) {
      return <ErrorNotice>{problemText(problem, strings)}</ErrorNotice>;
    }
    return (
      <Results
        fmt={fmt}
        input={input}
        level={Number(level)}
        strings={strings}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TextField
          id={`${ID}-m`}
          label={strings.markedLabel}
          numeric
          onChange={setMarked}
          value={marked}
        />
        <TextField
          id={`${ID}-c`}
          label={strings.caughtLabel}
          numeric
          onChange={setCaught}
          value={caught}
        />
        <TextField
          id={`${ID}-r`}
          label={strings.recapturedLabel}
          numeric
          onChange={setRecaptured}
          value={recaptured}
        />
        <SelectField
          id={`${ID}-level`}
          label={strings.levelLabel}
          onChange={setLevel}
          options={[
            { value: "0.9", label: "90%" },
            { value: "0.95", label: "95%" },
            { value: "0.99", label: "99%" },
          ]}
          value={level}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body()}
      </section>
    </div>
  );
}

function Results({
  input,
  level,
  strings,
  fmt,
}: {
  input: RecaptureInput;
  level: number;
  strings: Strings;
  fmt: NumberFormat;
}) {
  const s = summarize(input, level);
  const one = (value: number) => fmt(value, { maximumFractionDigits: 1 });
  const two = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const percent = (value: number) =>
    fmt(value * 100, { maximumFractionDigits: 1 });
  const levelText = fmt(level * 100, { maximumFractionDigits: 1 });
  const bound = (interval: Interval) => [
    one(interval.lower),
    interval.upper === null ? strings.noUpper : one(interval.upper),
  ];
  const head = [strings.columnMethod, strings.columnLower, strings.columnUpper];
  const rows = [
    [strings.methodNormal, ...bound(s.normal)],
    [strings.methodExact, ...bound(s.exact)],
  ];
  const vars = {
    m: fmt(input.marked),
    c: fmt(input.caught),
    r: fmt(input.recaptured),
  };
  const copy = t(strings.copySummary, {
    ...vars,
    chapman: one(s.chapman),
    level: levelText,
    lower: one(s.exact.lower),
    upper: s.exact.upper === null ? strings.noUpper : one(s.exact.upper),
  });
  return (
    <div className="space-y-6">
      <StatGrid>
        <StatCard emphasis label={strings.chapman} value={one(s.chapman)} />
        <StatCard
          label={strings.petersen}
          value={s.petersen === null ? strings.petersenNone : one(s.petersen)}
        />
        <StatCard label={strings.standardError} value={one(s.chapmanSe)} />
        <StatCard label={strings.seen} value={fmt(s.seen)} />
        <StatCard
          label={strings.markedShare}
          value={`${percent(s.markedShare)}%`}
        />
        <StatCard
          label={strings.populationMarked}
          value={`${percent(s.populationMarked)}%`}
        />
      </StatGrid>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>
          {t(strings.intervalsHeading, { level: levelText })}
        </h3>
        <DataTable head={head} rows={rows} />
      </div>
      <Notes input={input} percent={percent} s={s} strings={strings} />
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <ul className="space-y-1 font-mono text-foreground text-sm">
          {s.petersen === null ? null : (
            <li>
              {t(strings.workingPetersen, { ...vars, result: two(s.petersen) })}
            </li>
          )}
          <li>
            {t(strings.workingChapman, { ...vars, result: two(s.chapman) })}
          </li>
          <li>
            {t(strings.workingVariance, {
              variance: two(s.chapmanVariance),
              se: two(s.chapmanSe),
            })}
          </li>
        </ul>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        text={copy}
      />
      <p className={HINT}>{strings.assumptions}</p>
    </div>
  );
}

function Notes({
  input,
  s,
  percent,
  strings,
}: {
  input: RecaptureInput;
  s: ReturnType<typeof summarize>;
  percent: (value: number) => string;
  strings: Strings;
}) {
  const notes: string[] = [];
  if (input.recaptured === 0) {
    notes.push(strings.noteZero);
  } else {
    if (input.recaptured < FEW_RECAPTURES) {
      notes.push(t(strings.noteFew, { r: String(input.recaptured) }));
    }
    if (s.relativeError !== null) {
      notes.push(
        t(strings.notePrecision, { percent: percent(s.relativeError) })
      );
    }
  }
  notes.push(s.unbiased ? strings.noteUnbiased : strings.noteBiased);
  return (
    <div className="space-y-2">
      {notes.map((note) => (
        <Notice key={note}>{note}</Notice>
      ))}
    </div>
  );
}
